import React, { useState, useEffect, useContext, useRef } from 'react';
import './styles.scss';
import { images } from 'common';
import { ApplicationContext } from 'shared';

// ── Distance helper (Haversine) ───────────────────────────────────────────────
function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Star renderer ─────────────────────────────────────────────────────────────
function Stars({ rating }) {
  if (!rating) return <span style={{ color: '#9ca3af', fontSize: 12 }}>No rating</span>;
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {[...Array(5)].map((_, i) => (
        <span
          key={i}
          style={{
            fontSize: 13,
            color: i < full ? '#f59e0b' : i === full && half ? '#f59e0b' : '#d1d5db',
          }}
        >
          {i < full ? '★' : i === full && half ? '½' : '★'}
        </span>
      ))}
      <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 3 }}>{rating.toFixed(1)}</span>
    </span>
  );
}

// ── List view ─────────────────────────────────────────────────────────────────
function PlacesList({ places, currentLocation, loading }) {
  const sorted = [...places].sort((a, b) => {
    const da = distanceKm(currentLocation.lat, currentLocation.lng, a.lat, a.lng);
    const db = distanceKm(currentLocation.lat, currentLocation.lng, b.lat, b.lng);
    return da - db;
  });

  if (loading || places.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '60vh',
          gap: 12,
          color: '#6b7280',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #2563EB',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <p style={{ margin: 0, fontSize: 15 }}>
          {places.length === 0 ? 'Searching nearby places…' : 'Loading…'}
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        overflowY: 'auto',
        height: 'calc(100vh - 80px)',
        background: '#F7F8FA',
        padding: '20px 16px',
      }}
    >
      <p
        style={{
          margin: '0 0 16px 0',
          fontSize: 13,
          color: '#6b7280',
          fontWeight: 500,
        }}
      >
        {sorted.length} places found near you · sorted by distance
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sorted.map((place, idx) => {
          const km = distanceKm(currentLocation.lat, currentLocation.lng, place.lat, place.lng);
          const isHospital = place.type === 'hospital';
          const typeColor = isHospital ? '#ef4444' : '#2563EB';
          const typeBg = isHospital ? '#fee2e2' : '#dbeafe';
          const typeLabel = isHospital ? 'Hospital' : 'Pharmacy';

          return (
            <div
              key={place.placeId || idx}
              style={{
                background: 'white',
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                padding: '14px 16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: typeBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: 20,
                }}
              >
                {isHospital ? '🏥' : '💊'}
              </div>

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginBottom: 3,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: '#111827',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 220,
                    }}
                  >
                    {place.name}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: typeColor,
                      background: typeBg,
                      padding: '2px 7px',
                      borderRadius: 6,
                      flexShrink: 0,
                    }}
                  >
                    {typeLabel}
                  </span>
                  {place.isOpen === true && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#065f46',
                        background: '#d1fae5',
                        padding: '2px 7px',
                        borderRadius: 6,
                        flexShrink: 0,
                      }}
                    >
                      Open
                    </span>
                  )}
                  {place.isOpen === false && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#991b1b',
                        background: '#fee2e2',
                        padding: '2px 7px',
                        borderRadius: 6,
                        flexShrink: 0,
                      }}
                    >
                      Closed
                    </span>
                  )}
                </div>

                {place.address && (
                  <p
                    style={{
                      margin: '2px 0 4px 0',
                      fontSize: 13,
                      color: '#6b7280',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    📍 {place.address}
                  </p>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Stars rating={place.rating} />
                  {place.reviewCount > 0 && (
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                      ({place.reviewCount.toLocaleString()})
                    </span>
                  )}
                </div>
              </div>

              {/* Distance badge */}
              <div
                style={{
                  flexShrink: 0,
                  textAlign: 'right',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#374151',
                  }}
                >
                  {km < 1
                    ? `${Math.round(km * 1000)} m`
                    : `${km.toFixed(1)} km`}
                </span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    place.address || place.name
                  )}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{
                    fontSize: 12,
                    color: '#2563EB',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Directions →
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const Locations = () => {
  const { currentLocation, setCurrentLocation } = useContext(ApplicationContext);
  const [permissionState, setPermissionState] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'list'
  const [places, setPlaces] = useState([]);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const infoWindowRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fetchLocation, 1000);
  }, []);

  // --- 1. Get user location permission + coordinates ---
  const fetchLocation = () => {
    if ('geolocation' in navigator && 'permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        setPermissionState(status.state);
        status.onchange = function () {
          setPermissionState(this.state);
          if (this.state === 'granted') getLocation();
        };
        if (status.state === 'granted' || status.state === 'prompt') getLocation();
      });
    } else {
      console.error('Geolocation or Permissions API not supported by this browser.');
    }
  };

  const getLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setPermissionState('granted');
      },
      (err) => console.error('Error getting location:', err.message)
    );
  };

  // --- 2. Initialize Google Map + Markers when location ready ---
  useEffect(() => {
    if (
      currentLocation.lat &&
      currentLocation.lng &&
      permissionState === 'granted' &&
      window.google &&
      mapRef.current
    ) {
      if (!mapInstance.current) initMap();
    }
  }, [currentLocation, permissionState]);

  // --- 3. Initialize map and search nearby places ---
  const initMap = () => {
    const google = window.google;
    const userLatLng = new google.maps.LatLng(currentLocation.lat, currentLocation.lng);

    const map = new google.maps.Map(mapRef.current, {
      center: userLatLng,
      zoom: 16,
    });
    mapInstance.current = map;
    infoWindowRef.current = new google.maps.InfoWindow();

    new google.maps.Marker({
      position: userLatLng,
      map,
      icon: {
        url: images.bluePin,
        scaledSize: new google.maps.Size(20, 20),
      },
      title: 'Your Location',
    });

    ['hospital', 'pharmacy'].forEach((query) => searchPlaces(query, map));
  };

  // --- 4. Search, place markers, AND save results to state ---
  const searchPlaces = (query, map) => {
    const google = window.google;
    const service = new google.maps.places.PlacesService(map);
    const userLatLng = new google.maps.LatLng(currentLocation.lat, currentLocation.lng);

    service.textSearch({ location: userLatLng, radius: 1000, query }, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results.length) {
        // Save to state for list view
        const newPlaces = results.map((place) => ({
          name: place.name,
          type: query,
          address: place.formatted_address,
          rating: place.rating,
          reviewCount: place.user_ratings_total,
          placeId: place.place_id,
          types: place.types || [],
          isOpen: place.opening_hours?.open_now,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        }));
        setPlaces((prev) => {
          const existing = new Set(prev.map((p) => p.placeId));
          return [...prev, ...newPlaces.filter((p) => !existing.has(p.placeId))];
        });

        // Add map markers
        results.forEach((place) => createMarker(place, map, service));
      }
    });
  };

  // --- 5. Create marker and handle click ---
  const createMarker = (place, map, service) => {
    const google = window.google;
    const marker = new google.maps.Marker({
      map,
      position: place.geometry.location,
      icon: {
        url: place.types.includes('hospital')
          ? images.hospitalIcon
          : place.types.includes('pharmacy')
          ? images.pharmacyIcon
          : images.bluePin,
        scaledSize: new google.maps.Size(75, 75),
      },
    });

    marker.addListener('click', () => {
      service.getDetails(
        {
          placeId: place.place_id,
          fields: [
            'name',
            'formatted_address',
            'geometry',
            'rating',
            'user_ratings_total',
            'opening_hours',
            'formatted_phone_number',
            'website',
            'photos',
            'reviews',
            'types',
            'address_components',
          ],
        },
        async (details, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            let countryCode = '';
            if (details.address_components) {
              const countryComp = details.address_components.find((c) =>
                c.types.includes('country')
              );
              if (countryComp) countryCode = countryComp.short_name;
            }

            let countryInfo = null;
            try {
              if (countryCode) {
                const response = await fetch(
                  `https://restcountries.com/v3.1/alpha/${countryCode}`
                );
                const data = await response.json();
                countryInfo = data[0];
              }
            } catch (err) {
              console.error('Error fetching country info:', err);
            }

            const dialCode =
              countryInfo?.idd?.root && countryInfo?.idd?.suffixes?.length
                ? `${countryInfo.idd.root}${countryInfo.idd.suffixes[0]}`
                : '';

            const content = `
              <div style="max-width: 280px; font-family: 'Segoe UI', Arial, sans-serif; color: #202124; background: #fff; border-radius: 12px; box-shadow: 0 4px 10px rgba(0,0,0,0.15); padding: 10px;">
                ${
                  details.photos && details.photos.length
                    ? `<img src="${details.photos[0].getUrl({ maxWidth: 280, maxHeight: 150 })}"
                        alt="Place photo"
                        style="width: 100%; height: 150px; object-fit: cover; border-radius: 10px; margin-bottom: 10px;" />`
                    : ''
                }
                <h3 style="margin: 0 0 6px 0; font-size: 16px; color: #1a73e8;">${details.name}</h3>
                ${
                  details.formatted_address
                    ? `<p style="margin: 4px 0;">📍 <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(details.formatted_address)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;color:#202124;">${details.formatted_address}</a></p>`
                    : ''
                }
                ${
                  details.formatted_phone_number && dialCode
                    ? `<p style="margin: 4px 0;">📞 <a href="tel:${dialCode}${details.formatted_phone_number.replace(/\D/g, '').slice(-10)}" style="text-decoration:none;color:#1a73e8;font-weight:500;">${dialCode} ${details.formatted_phone_number.replace(/\D/g, '').slice(-10)}</a></p>`
                    : ''
                }
                ${
                  details.website
                    ? `<p style="margin: 4px 0;">🌐 <a href="${details.website}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;color:#1a73e8;">Visit Website</a></p>`
                    : ''
                }
                ${
                  details.rating
                    ? `<div style="margin-top: 6px;">⭐ <strong>${details.rating}</strong> <span style="color:#5f6368;">(${details.user_ratings_total || 0} reviews)</span></div>`
                    : ''
                }
              </div>
            `;

            infoWindowRef.current.setContent(content);
            infoWindowRef.current.open(map, marker);
          }
        }
      );
    });
  };

  const locationReady = currentLocation.lat && currentLocation.lng;

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Location permission warning */}
      {permissionState !== 'granted' && (
        <div className='location-warning-container'>
          <div className='location-warning'>
            <strong>Allow location</strong> access to view nearby hospitals and pharmacies.
          </div>
        </div>
      )}

      {/* Map / List toggle — shown once location is granted */}
      {locationReady && (
        <div
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1001,
            display: 'flex',
            background: 'white',
            borderRadius: 10,
            boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
          }}
        >
          {[
            { mode: 'map', label: '🗺 Map' },
            { mode: 'list', label: '☰ List' },
          ].map(({ mode, label }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: '8px 18px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13,
                background: viewMode === mode ? '#2563EB' : 'white',
                color: viewMode === mode ? 'white' : '#374151',
                transition: 'background 0.15s, color 0.15s',
                fontFamily: 'inherit',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Google Map — always mounted so Places API can run; hidden in list mode */}
      <div
        id='map'
        ref={mapRef}
        style={{
          height: '100vh',
          display: locationReady && viewMode === 'map' ? 'block' : 'none',
        }}
      >
        <img
          src={images.wmcLogo}
          alt='Logo'
          style={{ zIndex: 1000, height: 100, width: 150 }}
        />
      </div>

      {/* List view */}
      {viewMode === 'list' && locationReady && (
        <PlacesList
          places={places}
          currentLocation={currentLocation}
          loading={places.length === 0}
        />
      )}
    </div>
  );
};

export default Locations;
